import { useMemo } from 'react';
import { t } from 'i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName, useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { isValidCageId } from '@/domain/cages';
import { useCommitImageFeedback } from '@/domain/imageFeedback';
import { fullSizeImageMapper, useGetWelfareImages } from '@/domain/images';
import {
    selectMeasurementSource,
    selectSnapshotMeasurementSource,
    useGetCageDailyLice,
    useGetCageLast200Lice,
    useGetCageWeeklyLice,
} from '@/domain/lice';
import { useImageState } from '@/hooks/image-state';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { dayToString } from '@/utils/date';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';

import CageLiceDetectionsPageContent from './CageLiceDetectionsPageContent';

function CageLiceDetectionsContainer({ cageId }: CagePageInterface) {
    const { client, stock } = useCage();
    const timezone = useCageTimezone();
    const cageName = useCageName();
    const { isOptoScaleAdmin } = usePermissions();

    const { from, to, daypartTag, selectedFishGroupId } = useCageFilter();

    const isChileanLice = isChileanLiceType(stock?.liceType);

    // This page has no source toggle, unlike the other lice pages, so the flag decides it
    // outright. It was held in `useState` whose setter nothing ever called.
    const measurementSource: 'raw' | 'legacy' = client?.featureFlags?.includes('LICE_LEGACY_VIEW')
        ? 'raw'
        : 'legacy';

    const dayFrom = from ? dayToString(from) : undefined;
    const dayTo = to ? dayToString(to) : undefined;

    const dailyLice = useGetCageDailyLice(
        cageId,
        { dayFrom, dayTo, dayPart: daypartTag, fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );
    const weeklyLice = useGetCageWeeklyLice(
        cageId,
        { dayFrom, dayTo, dayPart: daypartTag, fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );
    const last200 = useGetCageLast200Lice(
        cageId,
        { fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );

    const loading = last200.isLoading || dailyLice.isLoading || weeklyLice.isLoading;
    const error = last200.error || dailyLice.error || weeklyLice.error;

    /********** Loce detection images *********/

    const imagesFishGroup = usePenCohortFishGroup();
    const {
        data: imagesdata,
        isLoading: imagesIsLoading,
        isFetching: imagesIsFetching,
    } = useGetWelfareImages(
        cageId,
        { type: 'louse-image', tags: ['day'] },
        {
            enabled: isValidCageId(cageId),
            keepPreviousData: true,
            fishGroup: imagesFishGroup,
        }
    );

    const imageState = useImageState({
        data: imagesdata,
        // `useMemo`, not `useCallback`: the argument is a *call* that returns the mapper, not
        // the mapper's body. They are equivalent at runtime, but `useCallback` cannot see that
        // `cageId` is used, so the linter reads the dependency as unnecessary — and an autofix
        // dropping it pins the mapper to the pen it first mounted with, mapping later rows to
        // the previous pen's image URL.
        mapper: useMemo(() => fullSizeImageMapper(cageId), [cageId]),
        loading: imagesIsLoading,
        isFetching: imagesIsFetching,
        timezone: timezone,
        queryParam: 'imageId',
    });

    const onCommitImageFeedback = useCommitImageFeedback({ cageId });

    if (loading) {
        return <Spinner size="lg" fullScreen />;
    }
    // The page's title goes with the content, so the error state stands in as its h1.
    if (error) {
        return (
            <SomethingHappened
                headingLevel="h1"
                {...retryFailedQueries(last200, dailyLice, weeklyLice)}
            />
        );
    }

    return (
        <PageLayout title={cageName} subtitle={t('common.labels.detections')}>
            <CageLiceDetectionsPageContent
                liceCount={dailyLice.data.map((row) =>
                    selectMeasurementSource(row, measurementSource)
                )}
                imageState={imageState}
                isOptoScaleAdmin={isOptoScaleAdmin}
                last200={
                    last200.data
                        ? selectSnapshotMeasurementSource(last200.data, measurementSource)
                        : undefined
                }
                onCommitImageFeedback={onCommitImageFeedback}
                isChileanLice={isChileanLice}
            />
        </PageLayout>
    );
}

export default CageLiceDetectionsContainer;
